import { IconType } from "react-icons";
import { BiLogoMicrosoftTeams } from "react-icons/bi";
import { TbMail } from "react-icons/tb";
import {
    SiSlack,
    SiGmail,
    SiZoom,
    SiDiscord,
    SiJira,
    SiAsana,
    SiLinear,
    SiGithub,
    SiNotion,
    SiTrello,
    SiSalesforce,
    SiHubspot,
    SiZoho,
    SiZendesk,
    SiIntercom,
    SiHelpscout,
    SiGoogledrive,
    SiDropbox,
    SiConfluence,
    SiAirtable,
    SiFigma,
    SiClickup,
} from "react-icons/si";

export type ProviderCategory = "communication" | "project_management" | "crm" | "productivity";
export type ConnectionStatus = "idle" | "authorizing" | "connected";

export interface MockProvider {
    id: string;
    name: string;
    description: string;
    category: ProviderCategory;
    brandColor: string;
    icon: IconType;
    logoBgColor?: string;
}

export const categoryLabels: Record<ProviderCategory, string> = {
    communication: "Communication",
    project_management: "Project Management",
    crm: "CRM",
    productivity: "Productivity",
};

export const mockProviders: MockProvider[] = [
    // Communication
    {
        id: "slack",
        name: "Slack",
        description: "Send messages, manage channels, and automate workflows.",
        category: "communication",
        brandColor: "#4A154B",
        icon: SiSlack,
    },
    {
        id: "teams",
        name: "Microsoft Teams",
        description: "Chat, meetings, and collaboration within Microsoft 365.",
        category: "communication",
        brandColor: "#6264A7",
        icon: BiLogoMicrosoftTeams,
    },
    {
        id: "gmail",
        name: "Gmail",
        description: "Read, send, and manage emails from Gmail accounts.",
        category: "communication",
        brandColor: "#EA4335",
        icon: SiGmail,
        logoBgColor: "#F3F4F6",
    },
    {
        id: "outlook",
        name: "Outlook",
        description: "Access email, calendar, and contacts from Outlook.",
        category: "communication",
        brandColor: "#0078D4",
        icon: TbMail,
    },
    {
        id: "zoom",
        name: "Zoom",
        description: "Schedule and manage video meetings and webinars.",
        category: "communication",
        brandColor: "#2D8CFF",
        icon: SiZoom,
    },
    {
        id: "discord",
        name: "Discord",
        description: "Manage servers, channels, and community interactions.",
        category: "communication",
        brandColor: "#5865F2",
        icon: SiDiscord,
    },

    // Project Management
    {
        id: "jira",
        name: "Jira",
        description: "Track issues, manage sprints, and plan projects.",
        category: "project_management",
        brandColor: "#0052CC",
        icon: SiJira,
    },
    {
        id: "asana",
        name: "Asana",
        description: "Organize tasks, projects, and team collaboration.",
        category: "project_management",
        brandColor: "#F06A6A",
        icon: SiAsana,
    },
    {
        id: "linear",
        name: "Linear",
        description: "Streamlined issue tracking and project management.",
        category: "project_management",
        brandColor: "#5E6AD2",
        icon: SiLinear,
    },
    {
        id: "github",
        name: "GitHub",
        description: "Manage repositories, issues, and pull requests.",
        category: "project_management",
        brandColor: "#24292E",
        icon: SiGithub,
    },
    {
        id: "notion",
        name: "Notion",
        description: "All-in-one workspace for notes, docs, and projects.",
        category: "project_management",
        brandColor: "#000000",
        icon: SiNotion,
    },
    {
        id: "trello",
        name: "Trello",
        description: "Visual boards for organizing tasks and workflows.",
        category: "project_management",
        brandColor: "#0079BF",
        icon: SiTrello,
    },

    // CRM
    {
        id: "salesforce",
        name: "Salesforce",
        description: "Manage leads, opportunities, and customer relationships.",
        category: "crm",
        brandColor: "#00A1E0",
        icon: SiSalesforce,
    },
    {
        id: "hubspot",
        name: "HubSpot",
        description: "CRM, marketing, sales, and service platform.",
        category: "crm",
        brandColor: "#FF7A59",
        icon: SiHubspot,
    },
    {
        id: "zoho",
        name: "Zoho CRM",
        description: "Sales automation, analytics, and customer management.",
        category: "crm",
        brandColor: "#E42527",
        icon: SiZoho,
    },
    {
        id: "zendesk",
        name: "Zendesk",
        description: "Customer support ticketing and help desk.",
        category: "crm",
        brandColor: "#03363D",
        icon: SiZendesk,
    },
    {
        id: "intercom",
        name: "Intercom",
        description: "Customer messaging and engagement platform.",
        category: "crm",
        brandColor: "#6AFDEF",
        icon: SiIntercom,
    },
    {
        id: "helpscout",
        name: "Help Scout",
        description: "Shared inbox and help desk for customer support.",
        category: "crm",
        brandColor: "#1292EE",
        icon: SiHelpscout,
    },

    // Productivity
    {
        id: "google-drive",
        name: "Google Drive",
        description: "Store, share, and collaborate on files and documents.",
        category: "productivity",
        brandColor: "#0F9D58",
        icon: SiGoogledrive,
    },
    {
        id: "dropbox",
        name: "Dropbox",
        description: "Cloud file storage, sharing, and synchronization.",
        category: "productivity",
        brandColor: "#0061FF",
        icon: SiDropbox,
    },
    {
        id: "confluence",
        name: "Confluence",
        description: "Team wiki and knowledge base documentation.",
        category: "productivity",
        brandColor: "#172B4D",
        icon: SiConfluence,
    },
    {
        id: "airtable",
        name: "Airtable",
        description: "Spreadsheet-database hybrid for flexible data management.",
        category: "productivity",
        brandColor: "#FCBF49",
        icon: SiAirtable,
    },
    {
        id: "figma",
        name: "Figma",
        description: "Collaborative interface design and prototyping.",
        category: "productivity",
        brandColor: "#A259FF",
        icon: SiFigma,
    },
    {
        id: "clickup",
        name: "ClickUp",
        description: "All-in-one productivity and project management.",
        category: "productivity",
        brandColor: "#7B68EE",
        icon: SiClickup,
    },
];
